<template>
  <div class="results-view">
    <div class="container">
      <!-- 模型运行模块 -->
      <div class="card model-card">
        <div class="card-header">
          <i class="fas fa-cogs" style="font-size: 24px"></i>
          <h3 class="header-title">模型运行</h3>
        </div>
        <div class="card-body">
          <div class="model-controls">
            <!-- 情景选择 -->
            <div class="form-group">
              <select v-model="selectedModelScenario" class="form-select">
                <option value="CN60">碳中和情景</option>
              </select>
            </div>
            
            <!-- 运行按钮 -->
            <button type="button"
                    class="btn btn-primary" 
                    @click="runModel"
                    :disabled="!selectedModelScenario || isRunning">
              <i :class="['fas', isRunning ? 'fa-spinner fa-spin' : 'fa-play']"></i>
              {{ isRunning ? '运行中...' : '模型运行' }}
            </button>
          </div>

          <!-- 运行结果提示 -->
          <div class="result-message" v-if="showResult">
            <i class="fas fa-check-circle"></i>
            {{ selectedModelScenario === 'CN60' ? '碳中和情景' : selectedModelScenario }} 运行完成
          </div>
        </div>
      </div>

      <!-- 结果展示模块 -->
      <div class="card results-card">
        <div class="card-header">
          <i class="fas fa-chart-line"></i>
          <h3>结果展示</h3>
        </div>
        <div class="card-body">
          <!-- 筛选条件 -->
          <div class="filter-section">
            <!-- 情景选择 -->
            <div class="form-group scenario-group">
              <div class="input-group">
                <!-- 修复下拉框中的 v-for 和 v-if 混用问题 -->
                <select v-model="scenarioToAdd" class="form-select">
                  <option value="">选择情景</option>
                  <option v-for="scenario in availableRunScenarios" 
                          :key="scenario"
                          :value="scenario">
                    {{ scenario === 'CN60' ? '碳中和情景' : scenario }}
                  </option>
                </select>
                <button @click="addScenario" 
                        class="btn btn-primary"
                        :disabled="!canAddScenario">
                  <i class="fas fa-plus"></i>
                  <span style="font-size: 24px;">情景添加</span>
                </button>
              </div>
              <!-- 已选情景标签 -->
              <div class="selected-items">
                <div v-for="scenario in selectedScenarios" 
                     :key="scenario" 
                     class="item-tag">
                  <span class="tag-text">{{ scenario === 'CN60' ? '碳中和情景' : scenario }}</span>
                  <button class="remove-btn" @click="removeScenario(scenario)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- 地域范围选择 -->
            <div class="form-group">
              <select v-model="selectedScope" 
                      class="form-select"
                      :disabled="!selectedScenarios.length">
                <option value="">选择区域范围</option>
                <option value="national">全国</option>
                <option value="provincial">分省</option>
              </select>
            </div>

            <!-- 省份选择（仅当选择"分省"时显示） -->
            <div class="form-group" v-if="selectedScope === 'provincial'">
              <select v-model="selectedProvince" 
                      class="form-select">
                <option value="">选择省份</option>
                <option v-for="province in provinces" 
                        :key="province" 
                        :value="province">
                  {{ province }}
                </option>
              </select>
            </div>

            <!-- 分析类型选择 -->
            <div class="form-group">
              <select v-model="selectedAnalysisType" 
                      class="form-select"
                      :disabled="!isValidScope">
                <option value="">选择分析类型</option>
                <option value="total_fuel">整体燃料构成</option>
                <option value="sector_fuel">分部门燃料构成</option>
              </select>
            </div>

            <!-- 部门选择（仅当选择"分部门燃料构成"时显示） -->
            <div class="form-group" v-if="selectedAnalysisType === 'sector_fuel'">
              <select v-model="selectedSector" 
                      class="form-select">
                <option value="">选择部门</option>
                <option value="agriculture">农业</option>
                <option value="industry">工业</option>
                <option value="construction">建筑业</option>
                <option value="transport">交通运输</option>
                <option value="service">服务业</option>
                <option value="residential">居民生活</option>
              </select>
            </div>
          </div>

          <!-- 未选择情景时的提示 -->
          <div class="empty-state" v-if="!runScenarios.length">
            <i class="fas fa-info-circle"></i>
            <p>请先运行模型以查看结果</p>
            <p class="sub-text">点击"模型运行"按钮运行所需情景</p>
          </div>

          <!-- 图表容器 -->
          <div v-else class="chart-wrapper">
            <div ref="chartRef" class="chart-container"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入需要的模块
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import PageHeader from '@/components/PageHeader.vue'
import * as echarts from 'echarts'
// 导入能源消费数据
import energyData from '@/data/30pe_output_data.json'
// 导入axios发送HTTP请求
import axios from 'axios'

export default {
  name: 'ResultsView',
  components: {
    PageHeader
  },
  setup() {
    const store = useStore()
    
    // 修改初始值
    const selectedModelScenario = ref('CN60')
    const isRunning = ref(false)
    const showResult = ref(false)
    const scenarioToAdd = ref('')
    const selectedScenarios = ref([])  // 初始为空数组，不预选任何情景
    const runScenarios = ref([])  // 初始为空数组，表示还未运行任何情景
    const selectedProvince = ref('')
    const selectedSector = ref('')
    const chartRef = ref(null)
    const chart = ref(null)

    // 添加新的响应式变量
    const selectedScope = ref('')  // 地域范围选择（全国/分省）
    const selectedAnalysisType = ref('')  // 分析类型（整体/分部门）

    // 动态获取省份列表
    const provinces = Object.keys(energyData.CN60 || {}).sort()

    // 修改运行模型方法，调用后端API运行Python脚本
    const runModel = async (event) => {
      // 阻止默认事件
      if (event) event.preventDefault();
      
      const startTime = Date.now();
      isRunning.value = true;
      showResult.value = false;
      
      try {
        // 调用API运行模型
        const response = await axios.post('/api/run-pe30-model', {
          scenario: selectedModelScenario.value
        });
        
        console.log('模型运行结果:', response.data);
        
        // 计算已经过的时间
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, 2000 - elapsedTime);
        
        // 确保加载状态至少显示2秒
        await new Promise(resolve => setTimeout(resolve, remainingTime));
        
        // 处理响应
        if (response.data.success) {
          if (!runScenarios.value.includes(selectedModelScenario.value)) {
            runScenarios.value.push(selectedModelScenario.value);
          }
          
          showResult.value = true;
          
          setTimeout(() => {
            showResult.value = false;
          }, 3000);
        } else {
          console.error('模型运行失败:', response.data.error);
          alert('模型运行失败: ' + response.data.error);
        }
      } catch (error) {
        console.error('请求API出错:', error);
        alert('请求失败，请检查服务器是否正常运行');
      } finally {
        isRunning.value = false;
      }
    };

    // 添加一个计算属性来替代 v-for 和 v-if 的混用
    const availableRunScenarios = computed(() => {
      return runScenarios.value.filter(s => !selectedScenarios.value.includes(s))
    })

    // 修改可选情景列表
    const availableScenarios = computed(() => {
      return runScenarios.value.filter(s => !selectedScenarios.value.includes(s))
    })

    // 移除未使用的 unrunScenarios
    // const unrunScenarios = computed(() => {
    //   return []
    // })

    // 简化的数据处理函数，专门从30pe_output_data.json获取数据
    const processChartData = (scope, province, analysisType, sector) => {
      const years = ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060']
      
      try {
        // 全国范围数据
        if (scope === 'national') {
          // 全国总燃料构成
          if (analysisType === 'total_fuel') {
            return years.map(year => {
              // 初始化数据结构
              const yearData = {
                year,
                electricity: 0,
                hydrogen: 0,
                coal: 0,
                oil: 0,
                gas: 0,
                heat: 0
              }
              
              // 累加所有省份的all_sectors数据
              Object.keys(energyData.CN60).forEach(provinceName => {
                const allSectors = energyData.CN60[provinceName].all_sectors
                if (allSectors) {
                  yearData.electricity += Number(allSectors.electricity[year] || 0)
                  yearData.hydrogen += Number(allSectors.hydrogen[year] || 0)
                  yearData.coal += Number(allSectors.coal[year] || 0)
                  yearData.oil += Number(allSectors.oil[year] || 0)
                  yearData.gas += Number(allSectors.gas[year] || 0)
                  yearData.heat += Number(allSectors.heat[year] || 0)
                }
              })
              
              return yearData
            })
          }
          
          // 全国分部门燃料构成
          if (analysisType === 'sector_fuel' && sector) {
            return years.map(year => {
              const yearData = {
                year,
                electricity: 0,
                hydrogen: 0,
                coal: 0,
                oil: 0,
                gas: 0,
                heat: 0
              }
              
              // 累加所有省份特定部门的数据
              Object.keys(energyData.CN60).forEach(provinceName => {
                const sectorData = energyData.CN60[provinceName][sector]
                if (sectorData) {
                  yearData.electricity += Number(sectorData.electricity[year] || 0)
                  yearData.hydrogen += Number(sectorData.hydrogen[year] || 0)
                  yearData.coal += Number(sectorData.coal[year] || 0)
                  yearData.oil += Number(sectorData.oil[year] || 0)
                  yearData.gas += Number(sectorData.gas[year] || 0)
                  yearData.heat += Number(sectorData.heat[year] || 0)
                }
              })
              
              return yearData
            })
          }
        }
        
        // 省级数据
        if (scope === 'provincial' && province) {
          const provinceData = energyData.CN60[province]
          
          if (!provinceData) {
            console.error(`No data found for province: ${province}`)
            return []
          }
          
          // 省级总燃料构成
          if (analysisType === 'total_fuel') {
            return years.map(year => {
              const allSectors = provinceData.all_sectors
              return {
                year,
                electricity: Number(allSectors?.electricity?.[year] || 0),
                hydrogen: Number(allSectors?.hydrogen?.[year] || 0),
                coal: Number(allSectors?.coal?.[year] || 0),
                oil: Number(allSectors?.oil?.[year] || 0),
                gas: Number(allSectors?.gas?.[year] || 0),
                heat: Number(allSectors?.heat?.[year] || 0)
              }
            })
          }
          
          // 省级分部门燃料构成
          if (analysisType === 'sector_fuel' && sector) {
            if (!provinceData[sector]) {
              console.error(`No data found for sector: ${sector} in province: ${province}`)
              return []
            }
            
            return years.map(year => {
              const sectorData = provinceData[sector]
              return {
                year,
                electricity: Number(sectorData?.electricity?.[year] || 0),
                hydrogen: Number(sectorData?.hydrogen?.[year] || 0),
                coal: Number(sectorData?.coal?.[year] || 0),
                oil: Number(sectorData?.oil?.[year] || 0),
                gas: Number(sectorData?.gas?.[year] || 0),
                heat: Number(sectorData?.heat?.[year] || 0)
              }
            })
          }
        }
      } catch (error) {
        console.error('Error processing chart data:', error)
      }
      
      return []
    }

    // 更新图表
    const updateChart = () => {
      if (!chart.value) {
        initChart()
        return
      }

      // 如果基本条件不满足，清空图表
      if (!selectedScope.value || !selectedAnalysisType.value || 
          (selectedScope.value === 'provincial' && !selectedProvince.value) ||
          (selectedAnalysisType.value === 'sector_fuel' && !selectedSector.value)) {
        chart.value.clear()
        return
      }

      try {
        // 获取处理后的数据
        const chartData = processChartData(
          selectedScope.value,
          selectedProvince.value,
          selectedAnalysisType.value,
          selectedSector.value
        )

        if (!chartData || chartData.length === 0) {
          console.warn('No chart data available')
          chart.value.clear()
          return
        }

        // 准备图表配置
        const years = chartData.map(d => d.year)
        const series = [
          {
            name: '电',
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: chartData.map(d => d.electricity),
            itemStyle: { color: '#91cc75' }
          },
          {
            name: '氢',
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: chartData.map(d => d.hydrogen),
            itemStyle: { color: '#5470c6' }
          },
          {
            name: '煤',
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: chartData.map(d => d.coal),
            itemStyle: { color: '#333333' }
          },
          {
            name: '油',
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: chartData.map(d => d.oil),
            itemStyle: { color: '#ee6666' }
          },
          {
            name: '气',
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: chartData.map(d => d.gas),
            itemStyle: { color: '#73c0de' }
          },
          {
            name: '热',
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: chartData.map(d => d.heat),
            itemStyle: { color: '#fc8452' }
          }
        ]

        // 设置图表选项
        const option = {
          title: {
            text: getChartTitle(),
            left: 'center',
            top: 20,
            textStyle: {
              fontSize: 24,
              fontWeight: 'bold',
              color: '#2d3748'
            }
          },
          tooltip: {
            trigger: 'axis',
            show: true,
            axisPointer: {
              type: 'shadow'
            },
            formatter: function(params) {
              let year = params[0].axisValue;
              let total = 0;
              let result = `<div style="font-size:16px;font-weight:bold;margin-bottom:8px">${year}年</div>`;
              
              // 遍历每个系列的数据
              params.forEach(param => {
                if (param.value > 0) {  // 只显示大于0的值
                  total += param.value;
                  result += `
                    <div style="display:flex;justify-content:space-between;margin:4px 0">
                      <span>${param.seriesName}:</span>
                      <span style="margin-left:15px;font-weight:bold">
                        ${(param.value/10000).toFixed(2)}万吨标准煤
                      </span>
                    </div>`;
                }
              });
              
              // 添加总计
              result += `
                <div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee">
                  <span>总计:</span>
                  <span style="margin-left:15px;font-weight:bold">
                    ${(total/10000).toFixed(2)}万吨标准煤
                  </span>
                </div>`;
              
              return result;
            }
          },
          legend: {
            bottom: 10,
            left: 'center',
            itemWidth: 25,
            itemHeight: 14,
            textStyle: {
              fontSize: 24,
              color: '#4a5568',
              itemGap: 25
            },
          },
          grid: {
            left: '10%',
            right: '5%',
            bottom: '15%',
            top: '15%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: years,
            axisLabel: {
              fontSize: 20,
              interval: 0,
              color: '#4a5568'
            },
            axisLine: {
              lineStyle: {
                color: '#e2e8f0'
              }
            }
          },
          yAxis: {
            type: 'value',
            name: '（万吨标准煤）',
            nameLocation: 'end',
            nameGap: 15,
            nameTextStyle: {
              fontSize: 22,
              color: '#4a5568',
              align: 'right',
              padding: [5, 0, 0, 0]
            },
            axisLabel: {
              fontSize: 20,
              color: '#4a5568',
              formatter: value => (value/10000).toFixed(1)
            },
            splitLine: {
              lineStyle: {
                color: '#e2e8f0',
                type: 'dashed'
              }
            }
          },
          series: series.map(s => ({
            ...s,
            barWidth: '60%',
            label: {
              show: false,
              position: 'inside',
              fontSize: 18,
              color: '#fff',
              formatter: function(params) {
                if (params.value > 0) {
                  return ''
                }
                return ''
              }
            },
            emphasis: {
              focus: 'series',
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.2)'
              }
            }
          }))
        }

        // 设置图表
        chart.value.setOption(option)
      } catch (error) {
        console.error('Error updating chart:', error)
      }
    }

    // 获取图表标题
    const getChartTitle = () => {
      const scopeText = selectedScope.value === 'national' ? 
        '全国' : selectedProvince.value
      
      if (selectedAnalysisType.value === 'total_fuel') {
        return `${scopeText}：燃料构成`
      } else {
        const sectorNames = {
          agriculture: '农业',
          industry: '工业',
          construction: '建筑业',
          transport: '交通运输',
          service: '服务业',
          residential: '居民生活',
          others: '其他'
        }
        return `${scopeText}：${sectorNames[selectedSector.value] || selectedSector.value}部门燃料构成`      }
    }

    // 判断地域选择是否有效
    const isValidScope = computed(() => {
      if (!selectedScope.value) return false
      if (selectedScope.value === 'national') return true
      return selectedScope.value === 'provincial' && selectedProvince.value
    })

    // 监听变化
    watch(
      [selectedScope, selectedProvince, selectedAnalysisType, selectedSector],
      () => {
        nextTick(() => {
          updateChart()
        })
      },
      { deep: true }
    )

    // 组件挂载时初始化图表
    onMounted(() => {
      nextTick(() => {
        initChart()
      })
    })

    // 添加初始化函数
    const initChart = () => {
      if (!chartRef.value) return
      
      if (chart.value) {
        chart.value.dispose()
      }
      
      chart.value = echarts.init(chartRef.value)
      
      window.addEventListener('resize', () => {
        chart.value?.resize()
      })
    }

    // 修改可添加情景的条件
    const canAddScenario = computed(() => {
      return scenarioToAdd.value && !selectedScenarios.value.includes(scenarioToAdd.value)
    })
    
    function addScenario() {
      if (canAddScenario.value) {
        selectedScenarios.value.push(scenarioToAdd.value)
        scenarioToAdd.value = '' // 清空输入框
      }
    }
    
    function removeScenario(scenario) {
      // 不允许移除基准情景
      if (scenario !== 'CN60') {
        selectedScenarios.value = selectedScenarios.value.filter(s => s !== scenario)
      }
    }

    return {
      selectedModelScenario,
      isRunning,
      showResult,
      scenarioToAdd,
      selectedScenarios,
      selectedProvince,
      selectedSector,
      runScenarios,
      provinces,
      runModel,
      chartRef,
      // 移除这一行: unrunScenarios,
      availableScenarios,
      availableRunScenarios, // 添加新的计算属性
      canAddScenario,
      addScenario,
      removeScenario,
      selectedScope,
      selectedAnalysisType,
      isValidScope
    }
  }
}
</script>

<style scoped>
.results-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  animation: fadeIn 0.6s ease-out;
}

/* 卡片基础样式 */
.card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: linear-gradient(to right, #f8f9fa, #ffffff);
  border-bottom: 1px solid #eef2f7;
}

.card-header i {
  font-size: 32px;
  color: #1890ff;
}

.card-header h3 {
  font-size: 28px;
  color: #2d3748;
  font-weight: 600;
  margin: 0;
}

.card-body {
  padding: 30px;
}

/* 模型运行模块样式 */
.model-controls {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

.form-group {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-size: 20px;
  font-weight: 500;
  color: #1e4c6b;
}

/* 表单元素样式 */
.form-select {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 22px;
  color: #4a5568;
  background-color: white;
  transition: all 0.2s ease;
}

.form-select:hover {
  border-color: #40a9ff;
}

.form-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* 按钮样式 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 24px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  height: 52px;
}

.btn i {
  font-size: 22px;
}

.btn-primary {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: linear-gradient(135deg, #d9d9d9, #bfbfbf);
  cursor: not-allowed;
  transform: none;
}

/* 结果提示 */
.result-message {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: #f6ffed;
  border-radius: 8px;
  color: #52c41a;
  font-size: 20px;
  animation: fadeIn 0.3s ease-out;
}

.result-message i {
  font-size: 22px;
}

/* 图表控件 */
.chart-controls {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.chart-controls .form-select {
  width: 150px;
}

/* 图表容器 */
.chart-container {
  height: 600px;
  min-height: 500px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  padding: 20px;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }
  
  .model-controls {
    flex-direction: column;
    gap: 12px;
  }
  
  .chart-controls {
    flex-direction: column;
    margin-left: 0;
    margin-top: 12px;
  }
  
  .chart-container {
    height: 400px;
  }
}

/* 筛选区域样式 */
.filter-section {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 25px;
  margin-bottom: 30px;
}

/* 情景选择组样式 */
.scenario-group {
  grid-column: span 4;
}

/* 空状态提示 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #8c8c8c;
  text-align: center;
  background: #fafafa;
  border-radius: 12px;
  margin-top: 20px;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #d9d9d9;
}

.empty-state p {
  margin: 0;
  font-size: 20px;
}

.empty-state .sub-text {
  font-size: 18px;
  color: #bfbfbf;
  margin-top: 8px;
}

/* 图表包装器 */
.chart-wrapper {
  margin-top: 25px;
  padding: 0;
  background: transparent;
  box-shadow: none;
}

/* 添加运行状态动画 */
.model-card {
  position: relative;
}

.model-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #1890ff, #096dd9);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.model-card.is-running::after {
  transform: scaleX(1);
}

.result-message {
  transform: translateY(0);
  opacity: 1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.result-message.hide {
  transform: translateY(-10px);
  opacity: 0;
}

/* 优化图表容器样式 */
.chart-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.chart-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.chart-wrapper:hover::before {
  opacity: 1;
}

.form-select[multiple] {
  height: auto;
  min-height: 120px;
}

/* 已选情景标签样式 */
.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
  min-height: 50px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
}

.item-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: white;
  border: 1px solid #e6f7ff;
  border-radius: 6px;
  font-size: 20px;
  color: #1890ff;
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.1);
  animation: fadeIn 0.3s ease-out;
  transition: all 0.3s ease;
}

.item-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
}

.tag-text {
  margin-right: 8px;
}

.remove-btn {
  background: none;
  border: none;
  padding: 2px 6px;
  color: #999;
  cursor: pointer;
  transition: color 0.3s;
  border-radius: 4px;
}

.remove-btn:hover {
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
}

/* 响应式设计调整 */
@media (max-width: 768px) {
  .filter-section {
    grid-template-columns: 1fr;
  }
  
  .scenario-group {
    grid-column: span 1;
  }
}

/* 情景选择输入组样式 */
.input-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.input-group .form-select {
  flex: 1;
}

.input-group .btn {
  flex-shrink: 0;
  min-width: 100px;
}

/* 优化添加按钮样式 */
.input-group .btn-primary {
  height: 48px; /* 调整为与模型运行按钮一致的高度 */
  padding: 0 20px;
  border-radius: 8px;
  font-size: 24px; /* 调整为与模型运行按钮一致的字体大小 */
}

.header-title {
  font-size: 54px;
  margin: 0;
  font-weight: 600;
  color: #2d3748;
}
</style>

